import Checkbox from "@mui/material/Checkbox";
import Radio from "@mui/material/Radio";
import Tooltip from "@mui/material/Tooltip";
import { styled, type Theme } from "@mui/material/styles";
import {
  getIsRowSelected,
  getMRT_RowSelectionHandler,
  getMRT_SelectAllHandler,
  type MRT_Row,
  type MRT_RowData,
  type MRT_TableInstance,
} from "material-react-table";
import type { ChangeEvent, MouseEvent } from "react";

type InputSlotProps = Record<string, unknown> | ((ownerState: unknown) => Record<string, unknown>);
type LegacyCheckboxProps = {
  inputProps?: Record<string, unknown>;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  slotProps?: { input?: InputSlotProps; [key: string]: unknown };
  sx?: unknown;
  title?: string;
  [key: string]: unknown;
};

function parseFromValuesOrFunc<TArgs, TValue>(
  valueOrFunc: TValue | ((args: TArgs) => TValue) | undefined,
  args: TArgs,
): TValue | undefined {
  return typeof valueOrFunc === "function" ? (valueOrFunc as (args: TArgs) => TValue)(args) : valueOrFunc;
}

function mergeInputSlotProps(
  inputSlotProps: InputSlotProps | undefined,
  inputAriaLabel: string,
  legacyInputProps: Record<string, unknown> | undefined,
): InputSlotProps {
  const mergedInputSlotProps =
    typeof inputSlotProps === "function"
      ? (ownerState: unknown) => ({
          "aria-label": inputAriaLabel,
          ...legacyInputProps,
          ...inputSlotProps(ownerState),
        })
      : {
          "aria-label": inputAriaLabel,
          ...legacyInputProps,
          ...inputSlotProps,
        };

  return mergedInputSlotProps;
}

const DisabledControlTooltipAnchor = styled("span")({ display: "inline-flex" });

type TableSelectCheckboxProps<TData extends MRT_RowData> = {
  row?: MRT_Row<TData>;
  staticRowIndex?: number;
  table: MRT_TableInstance<TData>;
};

/**
 * MRT's own select checkbox, on MUI 9's slots: a row's tick, or with no `row` the table's select-all.
 * The table's row-select column draws it, and so does the card list's select-all (#7171), so a press
 * on either goes through the same handler and the same `muiSelectAllCheckboxProps`.
 */
export function TableSelectCheckbox<TData extends MRT_RowData>(props: Readonly<TableSelectCheckboxProps<TData>>) {
  "use no memo";

  const { row, staticRowIndex, table } = props;
  const {
    getState,
    options: {
      enableMultiRowSelection,
      localization,
      muiSelectAllCheckboxProps,
      muiSelectCheckboxProps,
      selectAllMode,
    },
  } = table;
  const { density, isLoading } = getState();
  const selectAll = !row;

  const allRowsSelected = selectAll
    ? selectAllMode === "page"
      ? table.getIsAllPageRowsSelected()
      : table.getIsAllRowsSelected()
    : undefined;
  const isChecked = selectAll ? allRowsSelected : getIsRowSelected({ row, table });
  const inputAriaLabel = selectAll ? localization.toggleSelectAll : localization.toggleSelectRow;

  const rawCheckboxProps = (
    selectAll
      ? parseFromValuesOrFunc(muiSelectAllCheckboxProps, { table })
      : parseFromValuesOrFunc(muiSelectCheckboxProps, {
          row,
          staticRowIndex,
          table,
        })
  ) as LegacyCheckboxProps | undefined;

  const {
    inputProps: legacyInputProps,
    onClick,
    slotProps,
    sx,
    title,
    ...checkboxProps
  } = rawCheckboxProps ?? {};

  const onSelectionChange = row
    ? getMRT_RowSelectionHandler({
        row,
        staticRowIndex,
        table,
      })
    : undefined;
  const onSelectAllChange = getMRT_SelectAllHandler({ table });

  const commonProps = {
    "aria-label": inputAriaLabel,
    checked: isChecked,
    disabled: isLoading || (row && !row.getCanSelect()) || row?.id === "mrt-row-create",
    onChange: (event: ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      if (selectAll) {
        onSelectAllChange(event);
      } else {
        onSelectionChange?.(event);
      }
    },
    size: (density === "compact" ? "small" : "medium") as "medium" | "small",
    ...checkboxProps,
    onClick: (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onClick?.(event);
    },
    slotProps: {
      ...slotProps,
      input: mergeInputSlotProps(slotProps?.input, inputAriaLabel, legacyInputProps),
    },
    sx: (theme: Theme) => ({
      height: density === "compact" ? "1.75rem" : "2.5rem",
      m: density !== "compact" ? "-0.4rem" : undefined,
      width: density === "compact" ? "1.75rem" : "2.5rem",
      zIndex: 0,
      ...(parseFromValuesOrFunc(sx, theme) as object),
    }),
    title: undefined,
  };

  const control =
    enableMultiRowSelection === false ? (
      <Radio {...commonProps} />
    ) : (
      <Checkbox
        indeterminate={
          !isChecked && selectAll ? table.getIsSomeRowsSelected() : row?.getIsSomeSelected() && row.getCanSelectSubRows()
        }
        {...commonProps}
      />
    );

  // A disabled control takes no pointer events, so a tooltip on it never opens. The span takes them
  // instead, which is how a row that cannot be selected says why (#6577). A click on it stops there,
  // as one on the control does, so it does not open the row.
  return (
    <Tooltip title={title ?? inputAriaLabel}>
      {commonProps.disabled ? (
        <DisabledControlTooltipAnchor onClick={(event) => event.stopPropagation()}>{control}</DisabledControlTooltipAnchor>
      ) : (
        control
      )}
    </Tooltip>
  );
}
